import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Space, Modal, Descriptions, Card, DatePicker, Select, Row, Col, message } from 'antd';
import { EyeOutlined, SearchOutlined, CheckOutlined, CarOutlined, CheckCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const RequestsPage: React.FC = () => {
  const [filters, setFilters] = useState<any>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [filters]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['requests', filters, pagination],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const offset = (pagination.current - 1) * pagination.pageSize;
      params.append('limit', String(pagination.pageSize));
      params.append('offset', String(offset));
      const response = await api.get(`/admin/service-requests?${params}`);
      return response.data;
    }
  });

  const acceptMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/service-requests/${id}/accept`);
      return response.data;
    },
    onSuccess: () => {
      message.success('Żądanie zostało przyjęte');
      refetch();
    },
    onError: () => {
      message.error('Nie udało się przyjąć żądania');
    }
  });

  const deliverMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/service-requests/${id}/deliver`);
      return response.data;
    },
    onSuccess: () => {
      message.success('Samochód został oznaczony jako dostarczony');
      refetch();
    },
    onError: () => {
      message.error('Nie udało się oznaczyć jako dostarczony');
    }
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/service-requests/${id}/complete`);
      return response.data;
    },
    onSuccess: () => {
      message.success('Żądanie zostało ukończone');
      refetch();
    },
    onError: () => {
      message.error('Nie udało się ukończyć żądania');
    }
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/service-requests/${id}/cancel`);
      return response.data;
    },
    onSuccess: () => {
      message.success('Żądanie zostało anulowane');
      refetch();
    },
    onError: () => {
      message.error('Nie udało się anulować żądania');
    }
  });

  const statusColors: Record<string, string> = {
    pending: 'default',
    accepted: 'processing',
    delivered: 'success',
    completed: 'green',
    cancelled: 'error',
    expired: 'warning',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => id.substring(0, 8),
    },
    {
      title: 'Samochód',
      key: 'car',
      render: (_: any, record: any) => `${record.car?.brand} ${record.car?.model} (${record.car?.licensePlate})`,
    },
    {
      title: 'Klient',
      key: 'client',
      render: (_: any, record: any) => `${record.client?.firstName} ${record.client?.lastName}`,
    },
    {
      title: 'Lokaj',
      key: 'valet',
      render: (_: any, record: any) => record.valet ? `${record.valet.firstName} ${record.valet.lastName}` : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={statusColors[status]}>{status}</Tag>,
    },
    {
      title: 'Utworzono',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      render: (date: any) => {
        if (!date) return '-';
        const d = date instanceof Date ? date : new Date(date);
        return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
      },
    },
    {
      title: 'Czas',
      dataIndex: 'actualPickupAt',
      key: 'completionTime',
      render: (_: any, record: any) => {
        if (!record.actualPickupAt || !record.requestedAt) return '-';
        const d1 = record.requestedAt instanceof Date ? record.requestedAt : new Date(record.requestedAt);
        const d2 = record.actualPickupAt instanceof Date ? record.actualPickupAt : new Date(record.actualPickupAt);
        const diff = d2.getTime() - d1.getTime();
        return `${Math.round(diff / 60000)} min`;
      },
    },
    {
      title: 'Akcje',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          {record.status === 'pending' && (
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckOutlined />}
              onClick={() => acceptMutation.mutate(record.id)}
              loading={acceptMutation.isPending}
              title="Symuluj: Przyjmij żądanie"
            />
          )}
          {record.status === 'accepted' && (
            <Button 
              type="primary" 
              size="small" 
              icon={<CarOutlined />}
              onClick={() => deliverMutation.mutate(record.id)}
              loading={deliverMutation.isPending}
              title="Symuluj: Oznacz jako dostarczony"
            />
          )}
          {record.status === 'delivered' && (
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckCircleOutlined />}
              onClick={() => completeMutation.mutate(record.id)}
              loading={completeMutation.isPending}
              title="Symuluj: Ukończ żądanie"
            />
          )}
          {['pending', 'accepted', 'delivered'].includes(record.status) && (
            <Button 
              danger 
              size="small" 
              icon={<CloseOutlined />}
              onClick={() => cancelMutation.mutate(record.id)}
              loading={cancelMutation.isPending}
              title="Symuluj: Anuluj żądanie"
            />
          )}
          <Button icon={<EyeOutlined />} size="small" onClick={() => { setSelectedRequest(record); setDetailsVisible(true); }} />
        </Space>
      ),
    },
  ];

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleDateChange = (dates: any) => {
    if (dates) {
      setFilters((prev: any) => ({ ...prev, startDate: dates[0].toISOString(), endDate: dates[1].toISOString() }));
    } else {
      setFilters((prev: any) => ({ ...prev, startDate: null, endDate: null }));
    }
  };

  return (
    <div>
      <h1>Żądania</h1>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col>
            <Space>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 150 }}
                onChange={(value) => handleFilterChange('status', value)}
              >
                <Option value="pending">Oczekujące</Option>
                <Option value="accepted">Przyjęte</Option>
                <Option value="delivered">Dostarczone</Option>
                <Option value="completed">Ukończone</Option>
                <Option value="cancelled">Anulowane</Option>
              </Select>
              <RangePicker onChange={handleDateChange} />
              <Button icon={<SearchOutlined />} onClick={() => refetch()}>Filtruj</Button>
            </Space>
          </Col>
        </Row>
      </Card>
      <Table
        dataSource={data?.requests || []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{
          ...pagination,
          total: data?.total || 0,
          showSizeChanger: true,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
        }}
      />
      <Modal
        title="Szczegóły żądania"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRequest && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedRequest.id}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColors[selectedRequest.status]}>{selectedRequest.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Samochód">
              {selectedRequest.car?.brand} {selectedRequest.car?.model} ({selectedRequest.car?.licensePlate})
            </Descriptions.Item>
            <Descriptions.Item label="Klient">
              {selectedRequest.client?.firstName} {selectedRequest.client?.lastName}
              <br />
              {selectedRequest.client?.email}
            </Descriptions.Item>
            <Descriptions.Item label="Lokaj">
              {selectedRequest.valet ? `${selectedRequest.valet.firstName} ${selectedRequest.valet.lastName}` : 'Nieprzypisane'}
            </Descriptions.Item>
            <Descriptions.Item label="Miejsce parkingowe">{selectedRequest.parkingSpot || '-'}</Descriptions.Item>
            <Descriptions.Item label="Szacowany czas">{selectedRequest.estimatedTimeMinutes || '-'} min</Descriptions.Item>
            <Descriptions.Item label="Utworzono">{
              (() => {
                if (!selectedRequest.requestedAt) return '-';
                const d = selectedRequest.requestedAt instanceof Date ? selectedRequest.requestedAt : new Date(selectedRequest.requestedAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label="Zaktualizowano">{
              (() => {
                if (!selectedRequest.updatedAt) return '-';
                const d = selectedRequest.updatedAt instanceof Date ? selectedRequest.updatedAt : new Date(selectedRequest.updatedAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label="Przyjęto (accepted)">{
              (() => {
                if (!selectedRequest.acceptedAt) return '-';
                const d = selectedRequest.acceptedAt instanceof Date ? selectedRequest.acceptedAt : new Date(selectedRequest.acceptedAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label="Dostarczono (delivered)">{
              (() => {
                if (!selectedRequest.deliveredAt) return '-';
                const d = selectedRequest.deliveredAt instanceof Date ? selectedRequest.deliveredAt : new Date(selectedRequest.deliveredAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label="Deadline odbioru">{
              (() => {
                if (!selectedRequest.pickupDeadlineAt) return '-';
                const d = selectedRequest.pickupDeadlineAt instanceof Date ? selectedRequest.pickupDeadlineAt : new Date(selectedRequest.pickupDeadlineAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label="Zakończono (completed)">{
              (() => {
                if (!selectedRequest.actualPickupAt) return '-';
                const d = selectedRequest.actualPickupAt instanceof Date ? selectedRequest.actualPickupAt : new Date(selectedRequest.actualPickupAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label="Anulowano">{
              (() => {
                if (!selectedRequest.cancelledAt) return '-';
                const d = selectedRequest.cancelledAt instanceof Date ? selectedRequest.cancelledAt : new Date(selectedRequest.cancelledAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default RequestsPage;
